import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api, formatApiErrorDetail } from '../utils/api';
import { Save, Zap, Trash2, Plus, ChevronRight, Check, Copy, Layers } from 'lucide-react';
import { toast } from 'sonner';
import BACKEND_BASE from '../utils/backend';

const API_BASE = BACKEND_BASE;

const StepIcons = ({ steps, icons }) => {
  if (!steps || steps.length === 0) return <span className="text-xs" style={{ color: '#9CA3AF' }}>-</span>;
  const sorted = [...steps].sort((a, b) => (a.order || 0) - (b.order || 0));
  return (
    <div className="flex items-center gap-0.5 flex-wrap">
      {sorted.map((step, i) => {
        const icon = step.icon_id ? icons.find((ic) => ic.id === step.icon_id) : null;
        return (
          <div key={i} className="flex items-center gap-0.5">
            {i > 0 && <ChevronRight size={10} style={{ color: '#9CA3AF' }} />}
            <div className="flex items-center gap-0.5 px-1 py-0.5 rounded" style={{ backgroundColor: '#EFF6FF' }}
              title={step.step_name}>
              {icon?.image_url ? (
                <img src={API_BASE + icon.image_url} alt={icon.name} className="w-5 h-5 object-contain" />
              ) : null}
              <span className="text-xs whitespace-nowrap" style={{ color: '#1D4ED8' }}>{step.step_name}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PlanBuilder = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [zones, setZones] = useState([]);
  const [products, setProducts] = useState([]);
  const [methods, setMethods] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [icons, setIcons] = useState([]);
  const [surfaceItems, setSurfaceItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [planData, setPlanData] = useState({
    client_id: '',
    plan_name: '',
    valid_from: '',
  });
  const [selectedZones, setSelectedZones] = useState([]);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [planLines, setPlanLines] = useState([]);
  const [editingLineId, setEditingLineId] = useState(null);
  const [editData, setEditData] = useState({});
  // Multi-add modal state
  const [multiAddOpen, setMultiAddOpen] = useState(false);
  const [multiAddZoneId, setMultiAddZoneId] = useState('');
  const [multiAddSurfaceIds, setMultiAddSurfaceIds] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  // Alphabetically sorted derived lists (French collation, accent-insensitive).
  const zonesSorted = React.useMemo(
    () => [...zones].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
    ),
    [zones]
  );
  const surfacesSorted = React.useMemo(
    () => [...surfaceItems].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
    ),
    [surfaceItems]
  );

  const loadData = async () => {
    try {
      const [clientsRes, zonesRes, productsRes, methodsRes, freqRes, iconsRes, surfacesRes] = await Promise.all([
        api.get('/clients'),
        api.get('/zones'),
        api.get('/products'),
        api.get('/methods'),
        api.get('/frequencies'),
        api.get('/icons'),
        api.get('/surface-items'),
      ]);
      setClients(clientsRes.data);
      setZones(zonesRes.data);
      setProducts(productsRes.data);
      setMethods(methodsRes.data);
      setFrequencies(freqRes.data);
      setIcons(iconsRes.data);
      setSurfaceItems(surfacesRes.data);
    } catch (error) {
      toast.error('Echec du chargement des donnees');
    } finally {
      setLoading(false);
    }
  };

  // Handle when user picks a surface from the dropdown: auto-fill related fields
  const handleSurfaceChange = (surfaceId) => {
    const surface = surfaceItems.find((s) => s.id === surfaceId);
    const updates = { surface_id: surfaceId || null };
    if (surface) {
      updates.surface_item = surface.name;
      // Auto-fill icon override from the surface's default icon (mirrors the
      // PlanEditor surface->icon sync). Empty when the surface has no icon.
      updates.icon_id = surface.icon_id || null;
      // Auto-fill defaults if present (do NOT overwrite user-entered data if field is empty)
      if (surface.default_method_id && !editData.method_id) updates.method_id = surface.default_method_id;
      if (surface.default_product_id && !editData.product_id) {
        updates.product_id = surface.default_product_id;
        const prod = products.find((p) => p.id === surface.default_product_id);
        if (prod) {
          if (prod.dosage && !editData.dosage) updates.dosage = prod.dosage;
          if (prod.temperature && !editData.temperature_instruction) updates.temperature_instruction = prod.temperature;
          if (prod.default_method_id && !editData.method_id && !surface.default_method_id) {
            updates.method_id = prod.default_method_id;
          }
        }
      }
      if (surface.default_frequency_id && !editData.frequency_id) updates.frequency_id = surface.default_frequency_id;
      if (surface.default_dosage && !editData.dosage) updates.dosage = surface.default_dosage;
      if (surface.default_temperature && !editData.temperature_instruction) updates.temperature_instruction = surface.default_temperature;
      if (surface.default_control && !editData.control) updates.control = surface.default_control;
      if (surface.default_responsible && !editData.responsible) updates.responsible = surface.default_responsible;
    }
    setEditData((prev) => ({ ...prev, ...updates }));
  };

  // Bulk-create one plan_line per selected surface in a given zone. Triggered
  // from the "Ajouter plusieurs surfaces" modal in step 3.
  const handleMultiAddSurfaces = async () => {
    if (!multiAddZoneId) {
      toast.error('Selectionnez une zone');
      return;
    }
    if (multiAddSurfaceIds.length === 0) {
      toast.error('Selectionnez au moins une surface');
      return;
    }
    try {
      const baseOrder = planLines.reduce((max, l) => Math.max(max, l.sort_order || 0), 0);
      const tasks = multiAddSurfaceIds.map((sid, i) => {
        const surface = surfaceItems.find((s) => s.id === sid);
        const payload = {
          plan_id: currentPlanId,
          zone_id: multiAddZoneId,
          surface_item: surface?.name || '',
          surface_id: sid,
          icon_id: surface?.icon_id || null,
          product_id: '',
          dosage: '',
          temperature_instruction: '',
          method_id: '',
          frequency_id: '',
          control: '',
          responsible: '',
          sort_order: baseOrder + i + 1,
        };
        return api.post('/plan-lines', payload);
      });
      await Promise.all(tasks);
      toast.success(`${multiAddSurfaceIds.length} ligne(s) ajoutee(s)`);
      setMultiAddOpen(false);
      setMultiAddSurfaceIds([]);
      setMultiAddZoneId('');
      loadPlanLines();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Echec de l\'ajout multiple');
    }
  };

  const handleCreatePlan = async () => {
    if (!planData.client_id || !planData.plan_name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    try {
      const payload = {
        ...planData,
        valid_from: planData.valid_from ? new Date(planData.valid_from).toISOString() : null,
        generated_on: new Date().toISOString(),
      };
      const { data } = await api.post('/plans', payload);
      setCurrentPlanId(data.id);
      toast.success('Plan cree');
      setStep(2);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleAutoFill = async () => {
    if (selectedZones.length === 0) {
      toast.error('Selectionnez au moins une zone');
      return;
    }
    try {
      await api.post('/plan-lines/auto-fill', {
        plan_id: currentPlanId,
        zone_ids: selectedZones,
      });
      toast.success('Plan pre-rempli depuis les modeles (produit a selectionner)');
      loadPlanLines();
      setStep(3);
    } catch (error) {
      toast.error('Echec du pre-remplissage');
    }
  };

  const loadPlanLines = async () => {
    try {
      const { data } = await api.get(`/plan-lines/${currentPlanId}`);
      setPlanLines(data);
    } catch (error) {
      toast.error('Echec du chargement des lignes');
    }
  };

  const startEdit = (line) => {
    setEditingLineId(line.id);
    setEditData({
      plan_id: line.plan_id,
      zone_id: line.zone_id,
      surface_item: line.surface_item || '',
      surface_id: line.surface_id || '',
      product_id: line.product_id || '',
      dosage: line.dosage || '',
      temperature_instruction: line.temperature_instruction || '',
      method_id: line.method_id || '',
      frequency_id: line.frequency_id || '',
      control: line.control || '',
      responsible: line.responsible || '',
      sort_order: line.sort_order || 0,
    });
  };

  const handleProductChange = (productId) => {
    const product = products.find((p) => p.id === productId);
    const updates = { product_id: productId };

    if (product) {
      if (product.dosage) updates.dosage = product.dosage;
      if (product.temperature) updates.temperature_instruction = product.temperature;
      if (product.default_method_id) {
        updates.method_id = product.default_method_id;
      }
    }

    setEditData((prev) => ({ ...prev, ...updates }));
  };

  const handleSaveLine = async () => {
    if (!editData.surface_item && !editData.surface_id) {
      toast.error('Veuillez selectionner une surface');
      return;
    }
    try {
      if (editingLineId === 'new') {
        await api.post('/plan-lines', editData);
        toast.success('Ligne ajoutee');
      } else {
        await api.put(`/plan-lines/${editingLineId}`, editData);
        toast.success('Ligne mise a jour');
      }
      setEditingLineId(null);
      loadPlanLines();
    } catch (error) {
      toast.error('Echec de la sauvegarde');
    }
  };

  const handleDeleteLine = async (lineId) => {
    if (!window.confirm('Supprimer cette ligne ?')) return;
    try {
      await api.delete(`/plan-lines/${lineId}`);
      toast.success('Ligne supprimee');
      loadPlanLines();
    } catch (error) {
      toast.error('Echec de la suppression');
    }
  };

  const handleAddLine = (zoneId) => {
    const maxOrder = planLines.reduce((max, l) => Math.max(max, l.sort_order || 0), 0);
    setEditingLineId('new');
    setEditData({
      plan_id: currentPlanId,
      zone_id: zoneId || (selectedZones.length > 0 ? selectedZones[0] : ''),
      surface_item: '',
      surface_id: '',
      product_id: '',
      dosage: '',
      temperature_instruction: '',
      method_id: '',
      frequency_id: '',
      control: '',
      responsible: '',
      sort_order: maxOrder + 1,
    });
  };

  const handleDuplicateLine = async (line) => {
    try {
      const maxOrder = planLines.reduce((max, l) => Math.max(max, l.sort_order || 0), 0);
      const newLine = {
        plan_id: line.plan_id,
        zone_id: line.zone_id,
        surface_item: line.surface_item || '',
        surface_id: line.surface_id || '',
        product_id: line.product_id || '',
        dosage: line.dosage || '',
        temperature_instruction: line.temperature_instruction || '',
        method_id: line.method_id || '',
        frequency_id: line.frequency_id || '',
        control: line.control || '',
        responsible: line.responsible || '',
        sort_order: maxOrder + 1,
      };
      await api.post('/plan-lines', newLine);
      toast.success('Ligne dupliquee');
      loadPlanLines();
    } catch (error) {
      toast.error('Echec de la duplication');
    }
  };

  const handleFinish = () => {
    toast.success('Plan termine ! Vous pouvez le telecharger depuis la page Plans.');
    navigate('/plans');
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Chargement...</div>
      </Layout>
    );
  }

  const stepLabels = ['Informations', 'Zones', 'Lignes du plan'];

  return (
    <Layout>
      <div data-testid="plan-builder-page">
        <h1 className="text-3xl font-bold mb-8" style={{ color: '#0E2D43', fontFamily: 'Work Sans, sans-serif' }}>
          Creer un Plan
        </h1>

        {/* Stepper */}
        <div className="mb-8">
          <div className="flex items-center space-x-4">
            {[1, 2, 3].map((num) => (
              <div key={num} className="flex items-center">
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                  style={{ backgroundColor: step >= num ? '#0E2D43' : '#E5E7EB', color: step >= num ? '#FFFFFF' : '#6B7280' }}>
                  {step > num ? <Check size={18} /> : num}
                </div>
                {num < 3 && <div className="w-24 h-1 mx-2" style={{ backgroundColor: step > num ? '#0E2D43' : '#E5E7EB' }} />}
              </div>
            ))}
          </div>
          <div className="flex space-x-28 mt-2">
            {stepLabels.map((label, i) => (
              <span key={i} className="text-sm" style={{ color: step >= i + 1 ? '#0E2D43' : '#6B7280' }}>{label}</span>
            ))}
          </div>
        </div>

        {/* Step 1: Info */}
        {step === 1 && (
          <div className="bg-white rounded-lg shadow p-8 border" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold mb-6" style={{ color: '#0E2D43' }}>Etape 1 : Informations du plan</h2>
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Client *</label>
                <select value={planData.client_id}
                  onChange={(e) => setPlanData({ ...planData, client_id: e.target.value })}
                  data-testid="plan-client-select"
                  className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}>
                  <option value="">Selectionnez un client</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Nom du plan *</label>
                <input type="text" value={planData.plan_name}
                  onChange={(e) => setPlanData({ ...planData, plan_name: e.target.value })}
                  data-testid="plan-name-input"
                  className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}
                  placeholder="ex: Plan HACCP Avril 2026" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Valide a partir du</label>
                <input type="date" value={planData.valid_from}
                  onChange={(e) => setPlanData({ ...planData, valid_from: e.target.value })}
                  data-testid="plan-valid-from-input"
                  className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }} />
              </div>
            </div>
            <button onClick={handleCreatePlan} data-testid="create-plan-button"
              className="px-6 py-3 rounded font-medium" style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
              Continuer
            </button>
          </div>
        )}

        {/* Step 2: Zones */}
        {step === 2 && (
          <div className="bg-white rounded-lg shadow p-8 border" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold mb-6" style={{ color: '#0E2D43' }}>Etape 2 : Selection des zones</h2>
            <p className="mb-4" style={{ color: '#6B7280' }}>Selectionnez les zones a inclure dans ce plan</p>
            <div className="grid grid-cols-3 gap-4 mb-6">
              {zonesSorted.map((zone) => (
                <label key={zone.id} data-testid={`zone-checkbox-${zone.id}`}
                  className="flex items-center space-x-3 p-4 border rounded cursor-pointer transition-all"
                  style={{
                    borderColor: selectedZones.includes(zone.id) ? '#0E2D43' : '#D1D5DB',
                    backgroundColor: selectedZones.includes(zone.id) ? '#F0F9FF' : '#FFFFFF',
                  }}>
                  <input type="checkbox" checked={selectedZones.includes(zone.id)}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedZones([...selectedZones, zone.id]);
                      else setSelectedZones(selectedZones.filter((id) => id !== zone.id));
                    }}
                    className="w-5 h-5" />
                  <span style={{ color: '#0E2D43' }}>{zone.name}</span>
                </label>
              ))}
            </div>
            <button onClick={handleAutoFill} data-testid="auto-fill-button"
              className="flex items-center space-x-2 px-6 py-3 rounded font-medium"
              style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
              <Zap size={20} />
              <span>Pre-remplir depuis les modeles</span>
            </button>
          </div>
        )}

        {/* Step 3: Edit Lines */}
        {step === 3 && (
          <div className="bg-white rounded-lg shadow p-6 border" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold mb-2" style={{ color: '#0E2D43' }}>Etape 3 : Lignes du plan</h2>
            <p className="mb-6 text-sm" style={{ color: '#6B7280' }}>
              Selectionnez un produit pour chaque ligne. La methode sera remplie automatiquement.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full" style={{ minWidth: '1200px' }}>
                <thead style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium">Zone</th>
                    <th className="px-3 py-2 text-left text-xs font-medium">Surface</th>
                    <th className="px-3 py-2 text-left text-xs font-medium" style={{ minWidth: '180px' }}>Produit</th>
                    <th className="px-3 py-2 text-left text-xs font-medium">Dosage</th>
                    <th className="px-3 py-2 text-left text-xs font-medium">Temp.</th>
                    <th className="px-3 py-2 text-left text-xs font-medium" style={{ minWidth: '200px' }}>Methode</th>
                    <th className="px-3 py-2 text-left text-xs font-medium">Frequence</th>
                    <th className="px-3 py-2 text-left text-xs font-medium">Controle</th>
                    <th className="px-3 py-2 text-right text-xs font-medium" style={{ minWidth: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {planLines.map((line) => {
                    const zone = zones.find((z) => z.id === line.zone_id);
                    const isEditing = editingLineId === line.id;

                    if (isEditing) {
                      const selectedMethod = methods.find((m) => m.id === editData.method_id);
                      return (
                        <tr key={line.id} data-testid={`plan-line-edit-${line.id}`} className="border-t"
                          style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFF0' }}>
                          <td className="px-3 py-2 text-sm font-medium" style={{ color: '#0E2D43' }}>{zone?.name}</td>
                          <td className="px-3 py-2">
                            {(() => {
                              const surfacePool = surfacesSorted;
                              const currentMatchesSurfaceId = editData.surface_id && surfacePool.some(s => s.id === editData.surface_id);
                              // Legacy case: surface_item free text that doesn't match any id
                              const hasLegacyText = editData.surface_item && !currentMatchesSurfaceId;
                              return (
                                <div className="flex flex-col gap-1">
                                  <select
                                    value={editData.surface_id || ''}
                                    onChange={(e) => handleSurfaceChange(e.target.value)}
                                    data-testid={`edit-surface-select-${line.id}`}
                                    className="w-full px-2 py-1 border rounded text-sm font-medium"
                                    style={{ borderColor: editData.surface_id ? '#6FCF97' : '#FCD34D', backgroundColor: editData.surface_id ? '#F0FFF4' : '#FFFBEB' }}
                                  >
                                    <option value="">-- Choisir une surface --</option>
                                    {surfacePool.map((s) => (
                                      <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                    {hasLegacyText && (
                                      <option value="" disabled>-- texte libre ci-dessous --</option>
                                    )}
                                  </select>
                                  <input
                                    type="text"
                                    value={editData.surface_item}
                                    onChange={(e) => setEditData({ ...editData, surface_item: e.target.value })}
                                    data-testid={`edit-surface-text-${line.id}`}
                                    placeholder="Nom surface (ou texte libre)"
                                    className="w-full px-2 py-1 border rounded text-xs"
                                    style={{ borderColor: '#D1D5DB', backgroundColor: '#FAFAFA' }}
                                  />
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-2">
                            <select value={editData.product_id}
                              onChange={(e) => handleProductChange(e.target.value)}
                              data-testid={`edit-product-select-${line.id}`}
                              className="w-full px-2 py-1 border rounded text-sm font-medium"
                              style={{ borderColor: '#1D4ED8', backgroundColor: '#EFF6FF' }}>
                              <option value="">-- Selectionner --</option>
                              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input type="text" value={editData.dosage}
                              onChange={(e) => setEditData({ ...editData, dosage: e.target.value })}
                              className="w-full px-2 py-1 border rounded text-sm" style={{ borderColor: '#D1D5DB' }} />
                          </td>
                          <td className="px-3 py-2">
                            <input type="text" value={editData.temperature_instruction}
                              onChange={(e) => setEditData({ ...editData, temperature_instruction: e.target.value })}
                              className="w-full px-2 py-1 border rounded text-sm" style={{ borderColor: '#D1D5DB' }} />
                          </td>
                          <td className="px-3 py-2">
                            <select value={editData.method_id}
                              onChange={(e) => setEditData({ ...editData, method_id: e.target.value })}
                              data-testid={`edit-method-select-${line.id}`}
                              className="w-full px-2 py-1 border rounded text-sm mb-1" style={{ borderColor: '#D1D5DB' }}>
                              <option value="">-- Methode --</option>
                              {methods.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                            {selectedMethod && <StepIcons steps={selectedMethod.steps} icons={icons} />}
                          </td>
                          <td className="px-3 py-2">
                            <select value={editData.frequency_id}
                              onChange={(e) => setEditData({ ...editData, frequency_id: e.target.value })}
                              className="w-full px-2 py-1 border rounded text-sm" style={{ borderColor: '#D1D5DB' }}>
                              <option value="">-- Frequence --</option>
                              {frequencies.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input type="text" value={editData.control}
                              onChange={(e) => setEditData({ ...editData, control: e.target.value })}
                              className="w-full px-2 py-1 border rounded text-sm" style={{ borderColor: '#D1D5DB' }} />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={handleSaveLine} data-testid={`save-line-${line.id}`}
                                className="px-2 py-1 rounded text-xs font-medium"
                                style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>
                                <Save size={14} />
                              </button>
                              <button onClick={() => setEditingLineId(null)}
                                className="px-2 py-1 rounded text-xs"
                                style={{ backgroundColor: '#F3F4F6', color: '#374151' }}>
                                &times;
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    // Read-only row
                    const product = products.find((p) => p.id === line.product_id);
                    const method = methods.find((m) => m.id === line.method_id);
                    const freq = frequencies.find((f) => f.id === line.frequency_id);
                    const needsProduct = !line.product_id;

                    return (
                      <tr key={line.id} data-testid={`plan-line-${line.id}`}
                        className="border-t cursor-pointer hover:bg-gray-50 transition-colors"
                        style={{
                          borderColor: '#E5E7EB',
                          backgroundColor: needsProduct ? '#FFF7ED' : 'transparent',
                        }}
                        onClick={() => startEdit(line)}>
                        <td className="px-3 py-2 text-sm font-medium" style={{ color: '#0E2D43' }}>{zone?.name}</td>
                        <td className="px-3 py-2 text-sm" style={{ color: '#6B7280' }}>{line.surface_item}</td>
                        <td className="px-3 py-2 text-sm">
                          {product ? (
                            <span className="font-medium" style={{ color: '#0E2D43' }}>{product.name}</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-xs font-medium"
                              style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                              A selectionner
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm" style={{ color: '#6B7280' }}>{line.dosage || '-'}</td>
                        <td className="px-3 py-2 text-sm" style={{ color: '#6B7280' }}>{line.temperature_instruction || '-'}</td>
                        <td className="px-3 py-2">
                          {method ? (
                            <div>
                              <StepIcons steps={method.steps} icons={icons} />
                            </div>
                          ) : (
                            <span className="text-xs" style={{ color: '#9CA3AF' }}>-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm" style={{ color: '#6B7280' }}>{freq?.name || '-'}</td>
                        <td className="px-3 py-2 text-sm" style={{ color: '#6B7280' }}>{line.control || '-'}</td>
                        <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => handleDuplicateLine(line)}
                              data-testid={`duplicate-line-${line.id}`}
                              title="Dupliquer cette ligne"
                              className="inline-flex items-center px-2 py-1 rounded"
                              style={{ backgroundColor: '#DBEAFE', color: '#1D4ED8' }}>
                              <Copy size={14} />
                            </button>
                            <button onClick={() => handleDeleteLine(line.id)}
                              data-testid={`delete-line-${line.id}`}
                              className="inline-flex items-center px-2 py-1 rounded"
                              style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {/* New line row (when adding) */}
                  {editingLineId === 'new' && (
                    <tr data-testid="plan-line-new" className="border-t"
                      style={{ borderColor: '#E5E7EB', backgroundColor: '#F0FFF4' }}>
                      <td className="px-3 py-2">
                        <select value={editData.zone_id}
                          onChange={(e) => setEditData({ ...editData, zone_id: e.target.value })}
                          data-testid="new-line-zone-select"
                          className="w-full px-2 py-1 border rounded text-sm" style={{ borderColor: '#D1D5DB' }}>
                          <option value="">-- Zone --</option>
                          {selectedZones
                            .map((zId) => zonesSorted.find((z) => z.id === zId))
                            .filter(Boolean)
                            .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' }))
                            .map((z) => (
                              <option key={z.id} value={z.id}>{z.name}</option>
                            ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        {(() => {
                          const surfacePool = surfacesSorted;
                          return (
                            <div className="flex flex-col gap-1">
                              <select
                                value={editData.surface_id || ''}
                                onChange={(e) => handleSurfaceChange(e.target.value)}
                                data-testid="new-line-surface-select"
                                className="w-full px-2 py-1 border rounded text-sm font-medium"
                                style={{ borderColor: editData.surface_id ? '#6FCF97' : '#FCD34D', backgroundColor: editData.surface_id ? '#F0FFF4' : '#FFFBEB' }}
                              >
                                <option value="">-- Choisir une surface --</option>
                                {surfacePool.map((s) => (
                                  <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                              </select>
                              <input type="text" value={editData.surface_item}
                                onChange={(e) => setEditData({ ...editData, surface_item: e.target.value })}
                                placeholder="Ou texte libre"
                                data-testid="new-line-surface-text"
                                className="w-full px-2 py-1 border rounded text-xs"
                                style={{ borderColor: '#D1D5DB', backgroundColor: '#FAFAFA' }} />
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2">
                        <select value={editData.product_id}
                          onChange={(e) => handleProductChange(e.target.value)}
                          data-testid="new-line-product-select"
                          className="w-full px-2 py-1 border rounded text-sm font-medium"
                          style={{ borderColor: '#1D4ED8', backgroundColor: '#EFF6FF' }}>
                          <option value="">-- Produit --</option>
                          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input type="text" value={editData.dosage}
                          onChange={(e) => setEditData({ ...editData, dosage: e.target.value })}
                          placeholder="Dosage"
                          className="w-full px-2 py-1 border rounded text-sm" style={{ borderColor: '#D1D5DB' }} />
                      </td>
                      <td className="px-3 py-2">
                        <input type="text" value={editData.temperature_instruction}
                          onChange={(e) => setEditData({ ...editData, temperature_instruction: e.target.value })}
                          placeholder="Temp."
                          className="w-full px-2 py-1 border rounded text-sm" style={{ borderColor: '#D1D5DB' }} />
                      </td>
                      <td className="px-3 py-2">
                        <select value={editData.method_id}
                          onChange={(e) => setEditData({ ...editData, method_id: e.target.value })}
                          data-testid="new-line-method-select"
                          className="w-full px-2 py-1 border rounded text-sm mb-1" style={{ borderColor: '#D1D5DB' }}>
                          <option value="">-- Methode --</option>
                          {methods.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select value={editData.frequency_id}
                          onChange={(e) => setEditData({ ...editData, frequency_id: e.target.value })}
                          data-testid="new-line-frequency-select"
                          className="w-full px-2 py-1 border rounded text-sm" style={{ borderColor: '#D1D5DB' }}>
                          <option value="">-- Frequence --</option>
                          {frequencies.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input type="text" value={editData.control}
                          onChange={(e) => setEditData({ ...editData, control: e.target.value })}
                          placeholder="Controle"
                          className="w-full px-2 py-1 border rounded text-sm" style={{ borderColor: '#D1D5DB' }} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={handleSaveLine} data-testid="save-new-line"
                            className="px-2 py-1 rounded text-xs font-medium"
                            style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>
                            <Save size={14} />
                          </button>
                          <button onClick={() => setEditingLineId(null)}
                            className="px-2 py-1 rounded text-xs"
                            style={{ backgroundColor: '#F3F4F6', color: '#374151' }}>
                            &times;
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {planLines.length === 0 && editingLineId !== 'new' && (
              <p className="text-center py-8" style={{ color: '#6B7280' }}>
                Aucune ligne. Retournez en arriere pour pre-remplir depuis les modeles, ou ajoutez une ligne manuellement.
              </p>
            )}

            <div className="flex items-center gap-4 mt-4">
              <button onClick={() => handleAddLine()} data-testid="add-line-button"
                disabled={editingLineId !== null}
                className="flex items-center space-x-2 px-4 py-2 rounded font-medium border-2 border-dashed transition-colors"
                style={{
                  borderColor: editingLineId !== null ? '#D1D5DB' : '#0E2D43',
                  color: editingLineId !== null ? '#9CA3AF' : '#0E2D43',
                  backgroundColor: editingLineId !== null ? '#F9FAFB' : '#F0F9FF',
                  cursor: editingLineId !== null ? 'not-allowed' : 'pointer',
                }}>
                <Plus size={18} />
                <span>Ajouter une ligne</span>
              </button>

              <button onClick={() => { setMultiAddOpen(true); setMultiAddZoneId(selectedZones[0] || ''); }}
                data-testid="multi-add-surfaces-button"
                disabled={editingLineId !== null}
                className="flex items-center space-x-2 px-4 py-2 rounded font-medium border-2 transition-colors"
                style={{
                  borderColor: editingLineId !== null ? '#D1D5DB' : '#4A90D9',
                  color: editingLineId !== null ? '#9CA3AF' : '#1E40AF',
                  backgroundColor: editingLineId !== null ? '#F9FAFB' : '#EFF6FF',
                  cursor: editingLineId !== null ? 'not-allowed' : 'pointer',
                }}>
                <Layers size={18} />
                <span>Ajouter plusieurs surfaces</span>
              </button>

              <button onClick={handleFinish} data-testid="finish-plan-button"
                className="flex items-center space-x-2 px-6 py-3 rounded font-medium"
                style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
                <Save size={20} />
                <span>Terminer</span>
              </button>

              {planLines.some((l) => !l.product_id) && (
                <span className="text-sm" style={{ color: '#92400E' }}>
                  Certaines lignes n&apos;ont pas de produit selectionne
                </span>
              )}
            </div>

            {multiAddOpen && (
              <MultiAddSurfacesModal
                zones={zonesSorted.filter((z) => selectedZones.includes(z.id))}
                surfaces={surfacesSorted}
                selectedZoneId={multiAddZoneId}
                onZoneChange={setMultiAddZoneId}
                selectedSurfaceIds={multiAddSurfaceIds}
                onSurfaceToggle={(sid) => {
                  setMultiAddSurfaceIds((prev) =>
                    prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
                  );
                }}
                onSelectAll={() => setMultiAddSurfaceIds(surfacesSorted.map((s) => s.id))}
                onClear={() => setMultiAddSurfaceIds([])}
                onCancel={() => { setMultiAddOpen(false); setMultiAddSurfaceIds([]); }}
                onConfirm={handleMultiAddSurfaces}
              />
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PlanBuilder;


const MultiAddSurfacesModal = ({
  zones, surfaces, selectedZoneId, onZoneChange,
  selectedSurfaceIds, onSurfaceToggle, onSelectAll, onClear, onCancel, onConfirm,
}) => {
  const [query, setQuery] = React.useState('');

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return surfaces;
    return surfaces.filter((s) => (s.name || '').toLowerCase().includes(q));
  }, [query, surfaces]);

  return (
    <div
      data-testid="multi-add-modal"
      className="fixed inset-0 z-40 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.55)' }}
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b" style={{ borderColor: '#E5E7EB' }}>
          <h3 className="text-lg font-bold" style={{ color: '#0E2D43' }}>
            Ajouter plusieurs surfaces
          </h3>
          <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
            Selectionnez une zone et toutes les surfaces concernees. Une ligne sera creee par surface.
          </p>
        </div>

        <div className="px-6 py-4 grid grid-cols-2 gap-4 border-b" style={{ borderColor: '#E5E7EB' }}>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: '#0E2D43' }}>Zone *</label>
            <select
              value={selectedZoneId}
              onChange={(e) => onZoneChange(e.target.value)}
              data-testid="multi-add-zone-select"
              className="w-full px-2 py-1.5 border rounded text-sm"
              style={{ borderColor: '#D1D5DB' }}
            >
              <option value="">-- Choisir --</option>
              {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: '#0E2D43' }}>Recherche</label>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="multi-add-search"
              placeholder="Filtrer les surfaces..."
              className="w-full px-2 py-1.5 border rounded text-sm"
              style={{ borderColor: '#D1D5DB' }}
            />
          </div>
        </div>

        <div className="px-6 py-2 flex items-center justify-between border-b" style={{ borderColor: '#E5E7EB' }}>
          <span data-testid="multi-add-counter" className="text-sm font-medium" style={{ color: '#1E40AF' }}>
            {selectedSurfaceIds.length} surface(s) selectionnee(s)
          </span>
          <div className="flex items-center gap-2">
            <button onClick={onSelectAll}
              data-testid="multi-add-select-all"
              className="text-xs px-2 py-1 rounded border"
              style={{ borderColor: '#4A90D9', color: '#1E40AF' }}>
              Tout selectionner
            </button>
            <button onClick={onClear}
              data-testid="multi-add-clear"
              className="text-xs px-2 py-1 rounded border"
              style={{ borderColor: '#D1D5DB', color: '#6B7280' }}>
              Effacer
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2">
          {filtered.length === 0 ? (
            <p className="text-center py-6 text-sm" style={{ color: '#9CA3AF' }}>
              Aucune surface ne correspond.
            </p>
          ) : (
            <ul className="grid grid-cols-2 gap-1">
              {filtered.map((s) => {
                const checked = selectedSurfaceIds.includes(s.id);
                return (
                  <li key={s.id}>
                    <label
                      data-testid={`multi-add-surface-option-${s.id}`}
                      className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50"
                      style={{ backgroundColor: checked ? '#EFF6FF' : 'transparent' }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onSurfaceToggle(s.id)}
                        data-testid={`multi-add-surface-checkbox-${s.id}`}
                        className="w-4 h-4"
                      />
                      <span className="text-sm" style={{ color: '#0E2D43' }}>{s.name}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-6 py-3 flex items-center justify-end gap-2 border-t" style={{ borderColor: '#E5E7EB' }}>
          <button onClick={onCancel}
            data-testid="multi-add-cancel"
            className="px-4 py-2 rounded text-sm border"
            style={{ borderColor: '#D1D5DB', color: '#0E2D43' }}>
            Annuler
          </button>
          <button onClick={onConfirm}
            data-testid="multi-add-confirm"
            disabled={selectedSurfaceIds.length === 0 || !selectedZoneId}
            className="px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
            style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
            Ajouter ({selectedSurfaceIds.length})
          </button>
        </div>
      </div>
    </div>
  );
};
